import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { ProfileEditModal } from '../../components/connect/ProfileEditModal';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Users, UserPlus, UserMinus, Edit2, Star, MapPin, Truck, Package } from 'lucide-react';
import { useFeatures } from '../../contexts/FeatureContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ConnectProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatures();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const runnersEnabled = isFeatureEnabled('runners_service');
  const connectEnabled = isFeatureEnabled('connect_social');

  useEffect(() => {
    loadProfile();
  }, [username]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/social/user/profile/${username}`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
      } else {
        toast.error('User not found');
        navigate('/connect/feed');
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      setFollowLoading(true);
      const method = profile.is_following ? 'DELETE' : 'POST';
      const endpoint = profile.is_following 
        ? `/api/social/unfollow/${username}`
        : `/api/social/follow/${username}`;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method,
        credentials: 'include'
      });

      if (res.ok) {
        toast.success(profile.is_following ? 'Unfollowed' : 'Following');
        loadProfile();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to follow/unfollow');
      }
    } catch (error) {
      toast.error('Failed to follow/unfollow');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-black flex items-center justify-center">
        <div className="text-gray-500">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-black flex items-center justify-center">
        <div className="text-gray-500">Profile not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-black">
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/connect/feed')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Feed
        </Button>

        {/* Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <Avatar className="w-24 h-24">
                <AvatarImage src={profile.profile_picture} />
                <AvatarFallback className="text-2xl">
                  {profile.display_name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h1 className="text-2xl font-bold">{profile.display_name}</h1>
                    {profile.verified && (
                      <CheckCircle className="w-5 h-5 text-blue-500" />
                    )}
                    {profile.runner_info?.is_runner && runnersEnabled && (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        <Truck className="w-3 h-3 mr-1" />
                        Verified Runner
                      </Badge>
                    )}
                  </div>
                  <p className="text-gray-500">@{profile.username}</p>
                  {profile.bio && (
                    <p className="text-gray-700 dark:text-gray-300 mt-2">{profile.bio}</p>
                  )}
                  {profile.location && (
                    <p className="text-sm text-gray-500 mt-1">📍 {profile.location}</p>
                  )}
                </div>

                {/* Runner Info Card */}
                {profile.runner_info?.is_runner && runnersEnabled && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-3">
                      <Truck className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-green-900 dark:text-green-100">Runner Profile</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <div>
                          <p className="text-sm font-semibold">{profile.runner_info.rating?.toFixed(1) || '5.0'}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Rating</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="text-sm font-semibold">{profile.runner_info.completed_jobs || 0}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Jobs Done</p>
                        </div>
                      </div>
                    </div>

                    {profile.runner_info.services?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Services:</p>
                        <div className="flex flex-wrap gap-1">
                          {profile.runner_info.services.map((service, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {profile.runner_info.areas && (
                      <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>
                          {profile.runner_info.areas.area && `${profile.runner_info.areas.area}, `}
                          {profile.runner_info.areas.city}, {profile.runner_info.areas.country}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{profile.followers_count || 0}</div>
                    <div className="text-sm text-gray-500">Followers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{profile.following_count || 0}</div>
                    <div className="text-sm text-gray-500">Following</div>
                  </div>
                </div>

                {!profile.is_own_profile && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleFollow}
                      disabled={followLoading}
                      variant={profile.is_following ? 'outline' : 'default'}
                      className="flex-1"
                    >
                      {profile.is_following ? (
                        <>
                          <UserMinus className="w-4 h-4 mr-2" />
                          Unfollow
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Follow
                        </>
                      )}
                    </Button>
                    
                    {profile.runner_info?.is_runner && runnersEnabled && (
                      <Button
                        onClick={() => navigate('/pickup/post', { state: { preferredRunner: profile } })}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Request Runner
                      </Button>
                    )}
                  </div>
                )}
                
                {profile.is_own_profile && (
                  <Button onClick={() => setShowEditModal(true)} variant="outline">
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Posts */}
        <Card>
          <CardHeader>
            <CardTitle>Posts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              Posts from @{profile.username} will appear here
            </div>
          </CardContent>
        </Card>
      </div>
      
      <ProfileEditModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        profile={profile}
        onUpdate={loadProfile}
      />
    </div>
  );
}
